import React, { useState, useMemo } from 'react';
import {
    Card,
    Button,
    Input,
    Space,
    Tag,
    Typography,
    Select,
    DatePicker,
    Row,
    Col,
    Modal,
    Form,
    message,
    Dropdown,
    Tooltip,
    Alert,
    Segmented,
} from 'antd';
import {
    EyeOutlined,
    EditOutlined,
    MoreOutlined,
    FilterOutlined,
    ExportOutlined,
    ReloadOutlined,
    UserOutlined,
    ShopOutlined,
    FileTextOutlined,
    RiseOutlined,
    PlusOutlined,
    UserSwitchOutlined,
    ApartmentOutlined,
    TeamOutlined,
    DeleteOutlined,
    ExclamationCircleOutlined,
    ThunderboltOutlined,
} from '@ant-design/icons';
import {Link} from 'react-router-dom';
import type {ColumnsType} from 'antd/es/table';
import {useGetAdminOrganizationsQuery, useUpdateOrganizationStatusMutation, useCreateAdminOrganizationMutation, useGetAdminAnalyticsQuery, useDeleteOrganizationMutation, useStartAdminFreeTrialMutation, useGetAdminBenchmarkBusinessTypesQuery, useGetAdminBenchmarkProductTypesQuery, useGetQuestionTemplateTypesQuery, IQuestionTemplateType} from '../../services/nestjs-base-api';
import {useAuth} from '../../hooks/useAuth';
import {ImpersonationModal} from '../components/ImpersonationModal';
import { ResponsiveTable } from '../components/ResponsiveTable';
import { StatisticWithMock } from '../components/StatisticWithMock';
import {Utils} from '../../utils/utils';

const {Title, Text} = Typography;
const {Search} = Input;
const {Option} = Select;
const {RangePicker} = DatePicker;

// Using IOrganisation from API types

/**
 * Resolve a templateType from the selected benchmark business/product type slugs
 * against the config-driven registry (mirrors the agency create-client modal).
 * Exact bt+pt match first; else the first active row for the business type.
 */
function resolveTemplateType(
    registry: IQuestionTemplateType[],
    businessTypeSlug?: string,
    productTypeSlug?: string,
): string | undefined {
    if (!businessTypeSlug) return undefined;
    const exact = registry.find(
        (r) =>
            r.benchmarkBusinessTypeSlug === businessTypeSlug &&
            r.benchmarkProductTypeSlug === productTypeSlug,
    );
    if (exact) return exact.slug;
    const byBusinessType = registry
        .filter((r) => r.benchmarkBusinessTypeSlug === businessTypeSlug)
        .sort((a, b) => a.displayOrder - b.displayOrder);
    return byBusinessType[0]?.slug;
}

export const OrganizationsPage: React.FC = () => {
    const {user} = useAuth();
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [searchText, setSearchText] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [createModalVisible, setCreateModalVisible] = useState(false);
    const [impersonationModalVisible, setImpersonationModalVisible] = useState(false);
    const [selectedOrg, setSelectedOrg] = useState<any>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    const [form] = Form.useForm();
    const [createForm] = Form.useForm();
    
    const {data: organizationsData, isLoading, error, refetch} = useGetAdminOrganizationsQuery({
        jwtToken: user?.jwtToken || '',
        page: currentPage,
        limit: pageSize,
        search: searchText || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
    });
    
    const {data: analytics} = useGetAdminAnalyticsQuery({
        jwtToken: user?.jwtToken || '',
    });
    
    const [updateOrganizationStatus] = useUpdateOrganizationStatusMutation();
    const [createAdminOrganization] = useCreateAdminOrganizationMutation();

    // Business/product-type classification for the create-org modal (mirrors the
    // agency create-client modal). Only fetched while the modal is open.
    const {data: businessTypes} = useGetAdminBenchmarkBusinessTypesQuery(
        {jwtToken: user?.jwtToken || ''},
        {skip: !createModalVisible},
    );
    const {data: productTypes} = useGetAdminBenchmarkProductTypesQuery(
        {jwtToken: user?.jwtToken || ''},
        {skip: !createModalVisible},
    );
    const {data: templateTypeRegistry} = useGetQuestionTemplateTypesQuery(
        {jwtToken: user?.jwtToken || ''},
        {skip: !createModalVisible},
    );
    const selectedBusinessTypeId = Form.useWatch('businessTypeId', createForm);
    const filteredProductTypes = useMemo(() => {
        if (!productTypes || !selectedBusinessTypeId || !businessTypes) return [];
        const bt = businessTypes.find((b: any) => b.id === selectedBusinessTypeId);
        if (!bt?.productTypes) return productTypes;
        const validIds = bt.productTypes.map((pt: any) => pt.id);
        return productTypes.filter((pt: any) => validIds.includes(pt.id));
    }, [productTypes, selectedBusinessTypeId, businessTypes]);
    const [deleteOrganization, { isLoading: isDeleting }] = useDeleteOrganizationMutation();
    const [startFreeTrial, { isLoading: isStartingTrial }] = useStartAdminFreeTrialMutation();

    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');
    const [orgToDelete, setOrgToDelete] = useState<any>(null);
    // Charter Cohort (SYR-106) — which kind of organisation the create modal builds.
    const [createOrgKind, setCreateOrgKind] = useState<string>('standard');

    const organizations = useMemo(() => organizationsData?.data || [], [organizationsData?.data]);
    const totalOrganizations = organizationsData?.total || 0;
    
    // Debug: Log organizations data to see isMock flags
    React.useEffect(() => {
        if (organizations.length > 0) {
            console.log('Organizations data:', {
                count: organizations.length,
                sample: organizations.slice(0, 5).map(org => ({
                    name: org.name,
                    id: org.id,
                    isMock: org.isMock,
                    allKeys: Object.keys(org)
                }))
            });
        }
    }, [organizationsData?.data, organizations]);
    
    // Add CSS for mock data styling
    React.useEffect(() => {
        const style = document.createElement('style');
        style.textContent = `
            .mock-data-row {
                background-color: #fff7e6 !important;
                border-left: 4px solid #fa8c16 !important;
            }
            .mock-data-row:hover {
                background-color: #ffe7ba !important;
            }
            .mock-data-row td {
                background-color: inherit !important;
            }
        `;
        document.head.appendChild(style);
        return () => {
            if (document.head.contains(style)) {
                document.head.removeChild(style);
            }
        };
    }, []);
    
    if (error) {
        return (
            <div style={{padding: '24px'}}>
                <Alert
                    message="Error loading organizations"
                    description="Unable to fetch organizations data. Please try again later."
                    type="error"
                    showIcon
                    action={
                        <Button size="small" type="primary" onClick={() => refetch()}>
                            Retry
                        </Button>
                    }
                />
            </div>
        );
    }

    const handleStatusChange = (orgId: number, newStatus: string) => {
        Modal.confirm({
            title: 'Confirm Status Change',
            content: `Are you sure you want to change the organisation status to ${newStatus}?`,
            onOk: async () => {
                try {
                    await updateOrganizationStatus({
                        jwtToken: user?.jwtToken || '',
                        id: orgId,
                        status: newStatus,
                    }).unwrap();
                    message.success('Organisation status updated successfully');
                    refetch();
                } catch (error) {
                    message.error('Failed to update organisation status');
                }
            },
        });
    };

    const handleEdit = (org: any) => {
        setSelectedOrg(org);
        form.setFieldsValue({
            name: org.name,
            status: org.status,
        });
        setEditModalVisible(true);
    };

    const handleEditSave = async (values: any) => {
        if (!selectedOrg) return;
        
        try {
            await updateOrganizationStatus({
                jwtToken: user?.jwtToken || '',
                id: selectedOrg.id,
                status: values.status,
            }).unwrap();
            message.success('Organisation updated successfully');
            setEditModalVisible(false);
            form.resetFields();
            refetch();
        } catch (error) {
            message.error('Failed to update organisation');
        }
    };

    const handleCreateOrganization = async (values: any) => {
        // Derive templateType from the chosen benchmark business/product type so
        // the org is classified up-front (same as the agency create-client flow).
        const selectedBt = businessTypes?.find((b: any) => b.id === values.businessTypeId);
        const selectedPt = productTypes?.find((p: any) => p.id === values.benchmarkProductTypeId);
        const templateType = resolveTemplateType(
            templateTypeRegistry || [],
            selectedBt?.slug,
            selectedPt?.slug,
        );
        if (!templateType) {
            message.error('No template type is configured for the selected business/product type. Add a mapping under Statement Templates → Type Wiring.');
            return;
        }
        try {
            await createAdminOrganization({
                jwtToken: user?.jwtToken || '',
                payload: {
                    name: values.name,
                    billingEmail: values.billingEmail,
                    templateType,
                    businessTypeId: values.businessTypeId,
                    benchmarkProductTypeId: values.benchmarkProductTypeId || undefined,
                    orgKind: values.orgKind === 'charter' ? 'charter' : undefined,
                },
            }).unwrap();
            message.success(
                values.orgKind === 'charter'
                    ? 'Charter organisation created on the Charter plan'
                    : 'Organisation created successfully',
            );
            setCreateModalVisible(false);
            createForm.resetFields();
            setCreateOrgKind('standard');
            refetch();
        } catch (error) {
            message.error('Failed to create organisation');
        }
    };

    const handleImpersonate = (org: any) => {
        setSelectedOrg(org);
        setImpersonationModalVisible(true);
    };

    const handleStartTrial = (org: any) => {
        Modal.confirm({
            title: 'Start Free Trial',
            icon: <ThunderboltOutlined style={{ color: '#1890ff' }} />,
            content: (
                <div>
                    <p>This will start a free Professional trial for <strong>{org.name}</strong>.</p>
                    <ul style={{ paddingLeft: 20, marginTop: 12 }}>
                        <li>Upgrade subscription to Professional (trialing)</li>
                        <li>Allocate AI credits for the trial period</li>
                        <li>Send a welcome notification to the organisation</li>
                    </ul>
                </div>
            ),
            okText: 'Start Trial',
            onOk: async () => {
                try {
                    await startFreeTrial({
                        jwtToken: user?.jwtToken || '',
                        orgId: org.id,
                    }).unwrap();
                    message.success(`Free trial started for ${org.name}`);
                    refetch();
                } catch (error: any) {
                    message.error(error?.data?.message || 'Failed to start free trial');
                }
            },
        });
    };

    const handleDeleteOrganization = (org: any) => {
        setOrgToDelete(org);
        setDeleteConfirmText('');
        setDeleteModalVisible(true);
    };

    const handleDelete = async () => {
        if (deleteConfirmText !== orgToDelete?.name) {
            message.error('Organization name does not match');
            return;
        }

        try {
            await deleteOrganization({
                jwtToken: user?.jwtToken || '',
                id: orgToDelete.id,
            }).unwrap();
            message.success('Organization deleted successfully');
            setDeleteModalVisible(false);
            setOrgToDelete(null);
            setDeleteConfirmText('');
            refetch();
        } catch (error) {
            message.error('Failed to delete organization');
        }
    };

    const actionMenu = (record: any) => ({
        items: [
            {
                key: 'view',
                label: 'View Details',
                icon: <EyeOutlined />,
                onClick: () => {
                    window.location.href = `/admin/organizations/${record.id}`;
                },
            },
            {
                key: 'edit',
                label: 'Edit',
                icon: <EditOutlined />,
                onClick: () => handleEdit(record),
            },
            {
                type: 'divider' as const,
            },
            // Show "Start Trial" for orgs on a free plan with no active/trialing subscription
            ...(!record.subscription?.plan || record.subscription?.plan === 'Free' ? [
                {
                    key: 'start-trial',
                    label: 'Start Free Trial',
                    icon: <ThunderboltOutlined />,
                    onClick: () => handleStartTrial(record),
                },
                {
                    type: 'divider' as const,
                },
            ] : []),
            // Show impersonation for Admin and SuperAdmin users on active organizations with users
            ...(Utils.userIsPlatformAdmin(user) && record.status === 'ACTIVE' && record.userCount > 0 ? [
                {
                    key: 'impersonate',
                    label: 'Impersonate User',
                    icon: <UserSwitchOutlined />,
                    onClick: () => handleImpersonate(record),
                },
                {
                    type: 'divider' as const,
                }
            ] : []),
            {
                key: 'activate',
                label: 'Activate',
                disabled: record.status === 'active',
                onClick: () => handleStatusChange(record.id, 'active'),
            },
            {
                key: 'suspend',
                label: 'Suspend',
                disabled: record.status === 'suspended',
                onClick: () => handleStatusChange(record.id, 'suspended'),
            },
            {
                type: 'divider' as const,
            },
            {
                key: 'delete',
                label: 'Delete Organization',
                icon: <DeleteOutlined />,
                danger: true,
                onClick: () => handleDeleteOrganization(record),
            },
        ],
    });

    const columns: ColumnsType<any> = [
        {
            title: 'Organisation',
            dataIndex: 'name',
            key: 'name',
            render: (text: string, record: any) => (
                <div>
                    <Link to={`/admin/organizations/${record.id}`}>
                        <Text strong>{text}</Text>
                    </Link>
                    {record.orgType === 'agency' ? (
                        <>
                            {' '}
                            <Tag color="geekblue" icon={<TeamOutlined />} style={{fontSize: '10px', marginLeft: '8px'}}>
                                AGENCY
                            </Tag>
                        </>
                    ) : (
                        <>
                            {' '}
                            <Tag style={{fontSize: '10px', marginLeft: '8px'}}>
                                STANDARD
                            </Tag>
                        </>
                    )}
                    {record.isMock && (
                        <>
                            {' '}
                            <Tag color="orange" style={{fontSize: '10px', marginLeft: '8px'}}>
                                MOCK DATA
                            </Tag>
                        </>
                    )}
                    <br />
                    <Text type="secondary" style={{fontSize: '12px'}}>
                        ID: {record.id}
                    </Text>
                </div>
            ),
            sorter: (a, b) => a.name.localeCompare(b.name),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status: string) => (
                <Tag color={status === 'ACTIVE' ? 'green' : status === 'SUSPENDED' ? 'orange' : 'red'}>
                    {status.toUpperCase()}
                </Tag>
            ),
            filters: [
                {text: 'Active', value: 'ACTIVE'},
                {text: 'Suspended', value: 'SUSPENDED'},
                {text: 'Deleted', value: 'DELETED'},
            ],
            onFilter: (value, record) => record.status === value,
        },
        {
            title: 'Benchmark',
            dataIndex: 'benchmarkStatus',
            key: 'benchmarkStatus',
            render: (benchmarkStatus: string) => (
                <Tooltip title="Whether this organisation's responses feed the shared benchmark pool">
                    {benchmarkStatus === 'included' ? (
                        <Tag color="green">INCLUDED</Tag>
                    ) : benchmarkStatus === 'excluded' ? (
                        <Tag color="default">EXCLUDED</Tag>
                    ) : (
                        <Tag color="orange">PENDING</Tag>
                    )}
                </Tooltip>
            ),
            filters: [
                {text: 'Pending review', value: 'pending'},
                {text: 'Included', value: 'included'},
                {text: 'Excluded', value: 'excluded'},
            ],
            onFilter: (value, record) => (record.benchmarkStatus || 'pending') === value,
        },
        {
            title: 'Users',
            dataIndex: 'users',
            key: 'users',
            render: (_, record: any) => (
                <Tooltip title="Total users in organisation">
                    <Space>
                        <UserOutlined />
                        {record.userCount || 0}
                    </Space>
                </Tooltip>
            ),
            sorter: (a, b) => (a.userCount || 0) - (b.userCount || 0),
        },
        {
            title: 'Brands',
            dataIndex: 'brands',
            key: 'brands',
            render: (_, record: any) => (
                <Tooltip title="Total brands">
                    <Space>
                        <ShopOutlined />
                        {record.brandCount || 0}
                    </Space>
                </Tooltip>
            ),
            sorter: (a, b) => (a.brandCount || 0) - (b.brandCount || 0),
        },
        {
            title: 'Surveys',
            dataIndex: 'surveys',
            key: 'surveys',
            render: (_, record: any) => (
                <Tooltip title="Total surveys">
                    <Space>
                        <FileTextOutlined />
                        {record.surveyCount || 0}
                    </Space>
                </Tooltip>
            ),
            sorter: (a, b) => (a.surveyCount || 0) - (b.surveyCount || 0),
        },
        {
            title: 'Responses',
            dataIndex: 'responses',
            key: 'responses',
            render: (_, record: any) => (
                <Tooltip title="Total survey responses">
                    <Space>
                        <RiseOutlined />
                        {record.responseCount || 0}
                    </Space>
                </Tooltip>
            ),
            sorter: (a, b) => (a.responseCount || 0) - (b.responseCount || 0),
        },
        {
            title: 'Subscription',
            key: 'subscription',
            render: (_, record: any) => {
                const status = record.subscription?.status;
                const isFoundingCohort = status === 'founding_cohort' || record.subscription?.foundingCohort;

                let tagColor = 'red';
                let tagText = status || 'free';

                // Charter (SYR-106) reads as a plain active subscription
                // otherwise, which is correct but leaves the cohort unfindable
                // in a list of every org on the platform.
                const isCharter = !!record.charterJoinedAt;

                if (isFoundingCohort) {
                    tagColor = 'gold';
                    tagText = 'FOUNDING';
                } else if (isCharter) {
                    tagColor = 'gold';
                    tagText = 'CHARTER';
                } else if (status === 'active') {
                    tagColor = 'green';
                } else if (status === 'trialing') {
                    tagColor = 'blue';
                } else if (status === 'past_due') {
                    tagColor = 'orange';
                }

                const isFree = !record.subscription?.plan || record.subscription?.plan === 'Free';

                return (
                    <div>
                        <Text strong>{record.subscription?.plan || 'Free'}</Text>
                        <br />
                        <Tag color={tagColor} style={{fontSize: '10px'}}>
                            {tagText.toUpperCase()}
                        </Tag>
                        {/* How far through the trial, derived from the window
                            already on the payload — no extra request per row. */}
                        {status === 'trialing' && record.subscription?.trialStart && record.subscription?.trialEnd && (() => {
                            const start = new Date(record.subscription.trialStart).getTime();
                            const end = new Date(record.subscription.trialEnd).getTime();
                            const now = Date.now();
                            const totalDays = Math.max(1, Math.round((end - start) / 86400000));
                            const dayNumber = Math.max(0, Math.round((now - start) / 86400000));
                            const daysLeft = Math.max(0, Math.ceil((end - now) / 86400000));
                            return (
                                <>
                                    <br />
                                    <Text type="secondary" style={{ fontSize: '11px' }}>
                                        Day {Math.min(dayNumber, totalDays)}/{totalDays} · {daysLeft}d left
                                    </Text>
                                </>
                            );
                        })()}
                        {isFree && (
                            <>
                                <br />
                                <Button
                                    type="link"
                                    size="small"
                                    icon={<ThunderboltOutlined />}
                                    onClick={() => handleStartTrial(record)}
                                    loading={isStartingTrial}
                                    style={{ padding: 0, fontSize: '12px', height: 'auto' }}
                                >
                                    Start Trial
                                </Button>
                            </>
                        )}
                    </div>
                );
            },
        },
        {
            title: 'Created',
            dataIndex: 'createdAt',
            key: 'createdAt',
            sorter: (a, b) => new Date(a.createdAt || '').getTime() - new Date(b.createdAt || '').getTime(),
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record: any) => (
                <Dropdown menu={actionMenu(record)} trigger={['click']}>
                    <Button type="text" icon={<MoreOutlined />} />
                </Dropdown>
            ),
        },
    ];

    const rowSelection = {
        selectedRowKeys,
        onChange: setSelectedRowKeys,
    };

    return (
        <div style={{padding: '24px'}}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24}}>
                <Title level={2} style={{margin: 0}}>
                    <ApartmentOutlined style={{ marginRight: '8px' }} />
                    Organisations
                </Title>
                <Space>
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateModalVisible(true)}>
                        Add Organisation
                    </Button>
                    <Button icon={<ExportOutlined />}>
                        Export
                    </Button>
                    <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
                        Refresh
                    </Button>
                </Space>
            </div>

            {/* Summary Cards */}
            <Row gutter={[16, 16]} style={{marginBottom: 24}}>
                <Col xs={12} sm={6}>
                    <Card>
                        <StatisticWithMock
                            title="Total Organisations"
                            realValue={analytics?.totalOrganisations || 0}
                            mockValue={analytics?.mockOrganisations}
                            showMockData={analytics?.showMockData}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={6}>
                    <Card>
                        <StatisticWithMock
                            title="Active"
                            realValue={analytics?.activeOrganisations || 0}
                            mockValue={analytics?.mockActiveOrganisations}
                            showMockData={analytics?.showMockData}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={6}>
                    <Card>
                        <StatisticWithMock
                            title="Suspended"
                            realValue={organizations.filter(org => !org.isMock && org.status === 'SUSPENDED').length}
                            mockValue={organizations.filter(org => org.isMock && org.status === 'SUSPENDED').length}
                            showMockData={analytics?.showMockData}
                        />
                    </Card>
                </Col>
                <Col xs={12} sm={6}>
                    <Card>
                        <StatisticWithMock
                            title="Total Users"
                            realValue={analytics?.totalUsers || 0}
                            mockValue={analytics?.mockUsers}
                            showMockData={analytics?.showMockData}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Filters */}
            <Card style={{marginBottom: 16}}>
                <Row gutter={[16, 16]} align="middle">
                    <Col xs={24} sm={12} md={8}>
                        <Search
                            placeholder="Search organisations..."
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            onSearch={() => setCurrentPage(1)}
                            style={{width: '100%'}}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={4}>
                        <Select
                            placeholder="Status"
                            value={statusFilter}
                            onChange={setStatusFilter}
                            style={{width: '100%'}}
                        >
                            <Option value="all">All Status</Option>
                            <Option value="ACTIVE">Active</Option>
                            <Option value="SUSPENDED">Suspended</Option>
                            <Option value="DELETED">Deleted</Option>
                        </Select>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <RangePicker style={{width: '100%'}} />
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Space style={{width: '100%', justifyContent: 'flex-end'}}>
                            <Button icon={<FilterOutlined />}>
                                More Filters
                            </Button>
                        </Space>
                    </Col>
                </Row>
            </Card>

            {/* Organisations Table */}
            <Card>
                <ResponsiveTable
                    rowSelection={rowSelection}
                    columns={columns}
                    dataSource={organizations.map((org: any) => ({ ...org, key: org.id }))}
                    loading={isLoading}
                    rowClassName={(record: any) => record.isMock ? 'mock-data-row' : ''}
                    pagination={{
                        current: currentPage,
                        pageSize: pageSize,
                        total: totalOrganizations,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) =>
                            `${range[0]}-${range[1]} of ${total} organisations`,
                        onChange: (page, size) => {
                            setCurrentPage(page);
                            setPageSize(size || 10);
                        },
                    }}
                    size="small"
                />
            </Card>

            {/* Edit Modal */}
            <Modal
                title="Edit Organisation"
                open={editModalVisible}
                onCancel={() => setEditModalVisible(false)}
                footer={null}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleEditSave}
                >
                    <Form.Item
                        label="Organisation Name"
                        name="name"
                        rules={[{required: true, message: 'Please input organisation name!'}]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        label="Status"
                        name="status"
                        rules={[{required: true, message: 'Please select status!'}]}
                    >
                        <Select>
                            <Option value="ACTIVE">Active</Option>
                            <Option value="SUSPENDED">Suspended</Option>
                            <Option value="DELETED">Deleted</Option>
                        </Select>
                    </Form.Item>
                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit" loading={isLoading}>
                                Save Changes
                            </Button>
                            <Button onClick={() => setEditModalVisible(false)}>
                                Cancel
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Create Organisation Modal */}
            <Modal
                title="Add Organisation"
                open={createModalVisible}
                onCancel={() => setCreateModalVisible(false)}
                footer={null}
            >
                <Form
                    form={createForm}
                    layout="vertical"
                    onFinish={handleCreateOrganization}
                    initialValues={{ orgKind: 'standard' }}
                >
                    {/* Charter (SYR-106) is provisioned here, after the invoice
                        is paid, because a Charter brand never self-registers and
                        no Stripe webhook drives the plan change. */}
                    <Form.Item label="Organisation type" name="orgKind">
                        <Segmented
                            options={[
                                { label: 'Standard', value: 'standard' },
                                { label: 'Charter', value: 'charter' },
                            ]}
                            onChange={(value) => setCreateOrgKind(value as string)}
                        />
                    </Form.Item>
                    {createOrgKind === 'charter' && (
                        <Alert
                            type="warning"
                            showIcon
                            style={{ marginBottom: 16 }}
                            message="This creates a paying seat"
                            description={
                                <ul style={{ paddingLeft: 18, margin: '8px 0 0' }}>
                                    <li>Charter plan, $6,600 for the year, billed annually</li>
                                    <li>Joined today, first send committed within 30 days</li>
                                    <li>Renews in a year at the standard annual price</li>
                                    <li>Trial emails held, and no trial can be started for this org</li>
                                    <li>Benchmark pool stays Pending until you include it</li>
                                </ul>
                            }
                        />
                    )}
                    <Form.Item
                        label="Organisation Name"
                        name="name"
                        rules={[{required: true, message: 'Please input organisation name!'}]}
                    >
                        <Input placeholder="Enter organisation name" />
                    </Form.Item>
                    <Form.Item
                        label="Billing Email"
                        name="billingEmail"
                        rules={[
                            {type: 'email', message: 'Please enter a valid email!'},
                        ]}
                    >
                        <Input placeholder="Enter billing email (optional)" />
                    </Form.Item>
                    <Form.Item
                        label="Business Type"
                        name="businessTypeId"
                        rules={[{required: true, message: 'Please select a business type!'}]}
                    >
                        <Select
                            placeholder="Select business type"
                            onChange={() => createForm.setFieldValue('benchmarkProductTypeId', undefined)}
                            options={(businessTypes || []).map((bt: any) => ({value: bt.id, label: bt.name}))}
                        />
                    </Form.Item>
                    {filteredProductTypes.length > 0 && (
                        <Form.Item
                            label="Product Type"
                            name="benchmarkProductTypeId"
                        >
                            <Select
                                placeholder="Select product type"
                                allowClear
                                options={filteredProductTypes.map((pt: any) => ({value: pt.id, label: pt.name}))}
                            />
                        </Form.Item>
                    )}
                    <Form.Item>
                        <Space>
                            <Button type="primary" htmlType="submit" loading={isLoading}>
                                Create Organisation
                            </Button>
                            <Button onClick={() => setCreateModalVisible(false)}>
                                Cancel
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>

            {/* Impersonation Modal */}
            <ImpersonationModal
                visible={impersonationModalVisible}
                onCancel={() => setImpersonationModalVisible(false)}
                organization={selectedOrg}
            />

            {/* Delete Organization Modal */}
            <Modal
                title={
                    <span style={{ color: '#ff4d4f' }}>
                        <ExclamationCircleOutlined style={{ marginRight: 8 }} />
                        Delete Organization - Danger Zone
                    </span>
                }
                open={deleteModalVisible}
                onCancel={() => {
                    setDeleteModalVisible(false);
                    setDeleteConfirmText('');
                    setOrgToDelete(null);
                }}
                onOk={handleDelete}
                okText="Delete Permanently"
                okButtonProps={{ danger: true, disabled: deleteConfirmText !== orgToDelete?.name, loading: isDeleting }}
                cancelButtonProps={{ disabled: isDeleting }}
            >
                <Alert
                    message="⚠️ WARNING: This action cannot be undone!"
                    description={
                        <div>
                            <p style={{ marginBottom: 16, fontWeight: 500 }}>
                                <strong>Will be permanently deleted:</strong>
                            </p>
                            <ul style={{ marginBottom: 16 }}>
                                <li><strong>{orgToDelete?.brandCount || 0}</strong> brands</li>
                                <li><strong>{orgToDelete?.surveyCount || 0}</strong> surveys</li>
                                <li>All survey responses and analytics data</li>
                                <li>All questions, settings, and configurations</li>
                                <li>API keys and external metrics</li>
                            </ul>
                            <p style={{ marginBottom: 16, fontWeight: 500 }}>
                                <strong>Will be anonymized (kept for audit/compliance):</strong>
                            </p>
                            <ul style={{ marginBottom: 16 }}>
                                <li><strong>{orgToDelete?.userCount || 0}</strong> users (PII removed, marked as "Deleted User")</li>
                                <li>Organization record (renamed to "Deleted Organization #{orgToDelete?.id}")</li>
                                <li>Event logs, AI conversations, and billing records remain linked</li>
                            </ul>
                        </div>
                    }
                    type="error"
                    showIcon
                    style={{ marginBottom: 24 }}
                />
                <div>
                    <Typography.Text strong>
                        To confirm, type <span style={{ color: '#d32f2f' }}>{orgToDelete?.name}</span> in the field below:
                    </Typography.Text>
                    <Input
                        placeholder={orgToDelete?.name}
                        value={deleteConfirmText}
                        onChange={(e) => setDeleteConfirmText(e.target.value)}
                        style={{ marginTop: 8 }}
                        disabled={isDeleting}
                    />
                </div>
            </Modal>
        </div>
    );
};